<script setup lang="ts">
import { computed } from 'vue'

import BaseImage from '@/components/atoms/BaseImage.vue'
import type { BetaAdvertisement } from '@/types/beta-dashboard'

const props = defineProps<{
    advertisement: BetaAdvertisement
    updating?: boolean
    deleting?: boolean
}>()

const emit = defineEmits<{
    edit: [advertisement: BetaAdvertisement]
    toggle: [advertisement: BetaAdvertisement, status: boolean]
    remove: [advertisement: BetaAdvertisement]
}>()

const positionLabel = computed(() => ({
    menu: 'Menu do topo',
    after_content: 'Depois do conteúdo',
    footer: 'Rodapé',
    sidebar: 'Menu lateral',
}[props.advertisement.position]))

const typeLabel = computed(() => ({
    text: 'Texto',
    banner: 'Imagem',
    script: 'Código incorporado',
}[props.advertisement.advertisementType]))

const requestStatusChange = (event: Event) => {
    const input = event.target as HTMLInputElement
    emit('toggle', props.advertisement, input.checked)
    input.checked = props.advertisement.status
}
</script>

<template>
    <article class="beta-advertisement-card">
        <div :class="['beta-advertisement-card__preview', `is-${advertisement.advertisementType}`]">
            <BaseImage
                v-if="advertisement.advertisementType === 'banner' && advertisement.image"
                :src="advertisement.image"
                :alt="advertisement.title"
                class-name="beta-advertisement-card__image"
            />
            <span v-else aria-hidden="true">
                {{ advertisement.advertisementType === 'script' ? '&lt;/&gt;' : 'Aa' }}
            </span>
        </div>

        <div class="beta-advertisement-card__content">
            <div class="beta-advertisement-card__heading">
                <div>
                    <strong>{{ advertisement.title || 'Publicidade sem título' }}</strong>
                    <small>{{ positionLabel }} · {{ typeLabel }}</small>
                </div>
                <button type="button" class="is-edit" @click="emit('edit', advertisement)">
                    Editar
                </button>
            </div>

            <p v-if="advertisement.advertisementType === 'banner' && advertisement.ref">
                {{ advertisement.ref }}
            </p>
            <p v-else-if="advertisement.content">
                {{ advertisement.content }}
            </p>

            <div class="beta-advertisement-card__actions">
                <label>
                    <input
                        type="checkbox"
                        :checked="advertisement.status"
                        :disabled="updating"
                        @change="requestStatusChange"
                    >
                    <span>{{ updating ? 'Atualizando…' : advertisement.status ? 'Ativa' : 'Desativada' }}</span>
                </label>

                <button
                    type="button"
                    class="is-remove"
                    :disabled="deleting"
                    @click="emit('remove', advertisement)"
                >
                    {{ deleting ? 'Excluindo…' : 'Excluir' }}
                </button>
            </div>
        </div>
    </article>
</template>

<style scoped>
.beta-advertisement-card {
    display: grid;
    min-width: 0;
    grid-template-columns: 108px minmax(0, 1fr);
    overflow: hidden;
    border: 1px solid #e2e8f0;
    border-radius: 17px;
    background: #fff;
}

.beta-advertisement-card__preview {
    display: grid;
    min-height: 142px;
    place-items: center;
    overflow: hidden;
    background: #eef2f7;
    color: #7588a2;
    font-size: 24px;
    font-weight: 750;
}

.beta-advertisement-card__preview.is-script {
    background: #172d4a;
    color: #dbe7f5;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 15px;
}

.beta-advertisement-card__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.beta-advertisement-card__content {
    display: grid;
    min-width: 0;
    align-content: space-between;
    gap: 9px;
    padding: 13px;
}

.beta-advertisement-card__heading {
    display: flex;
    min-width: 0;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
}

.beta-advertisement-card__heading > div {
    min-width: 0;
}

.beta-advertisement-card__heading strong,
.beta-advertisement-card__heading small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.beta-advertisement-card__heading strong {
    color: #223a58;
    font-size: 12px;
    font-weight: 850;
}

.beta-advertisement-card__heading small {
    margin-top: 3px;
    color: #8493a7;
    font-size: 10px;
}

.beta-advertisement-card__heading .is-edit {
    flex: none;
    color: var(--main-color);
    font-size: 9px;
    font-weight: 850;
}

.beta-advertisement-card__content > p {
    display: -webkit-box;
    overflow: hidden;
    color: #75859b;
    font-size: 10px;
    line-height: 1.45;
    overflow-wrap: anywhere;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
}

.beta-advertisement-card__actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.beta-advertisement-card__actions label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #60738d;
    font-size: 12px;
    font-weight: 750;
    cursor: pointer;
}

.beta-advertisement-card__actions input {
    accent-color: var(--main-color);
}

.beta-advertisement-card__actions .is-remove {
    color: #b55454;
    font-size: 9px;
    font-weight: 800;
}

.beta-advertisement-card__actions button:disabled,
.beta-advertisement-card__actions input:disabled {
    cursor: wait;
    opacity: 0.55;
}

@media (max-width: 520px) {
    .beta-advertisement-card {
        grid-template-columns: 88px minmax(0, 1fr);
    }
}
</style>
